import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { OpenSheetMusicDisplay } from 'opensheetmusicdisplay';
import CollapsibleSection from './CollapsibleSection';
import { loadSoundfontInstrument, PURE_TONE, RICH_TONE } from '../utils/soundfontInstruments';
import { TUNINGS, intervalCentsOffset } from '../utils/tuning';
import { logEarTrainingStart, logEarTrainingComplete, logActivity, ACTIVITY_TYPES } from '../utils/activityLogger';
import * as Tone from 'tone';

const IntervalTraining = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  
  // Game state
  const [gameState, setGameState] = useState('setup'); // 'setup', 'playing', 'finished'
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(10);
  const [currentInterval, setCurrentInterval] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [roundResults, setRoundResults] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingSource, setPlayingSource] = useState(null); // 'A', 'B', or null
  const [hasGuessed, setHasGuessed] = useState(false);
  const [showInterval, setShowInterval] = useState(false);
  const [notationKey, setNotationKey] = useState(0);
  const [isInABMode, setIsInABMode] = useState(false);
  const [userGuessedInterval, setUserGuessedInterval] = useState(null);
  const [loadedInstrument, setLoadedInstrument] = useState(null);
  // Equal or just intonation for the second note; can be flipped at any time
  const [tuning, setTuning] = useState(TUNINGS.equal);
  const [currentRandomInstrument, setCurrentRandomInstrument] = useState(null);
  
  // Configuration
  const [config, setConfig] = useState({
    mode: 'mixed', // 'harmonic', 'melodic', 'mixed'
    questionCount: 10,
    enabledIntervals: new Set(['perfect4th', 'perfect5th', 'major3rd', 'minor3rd', 'octave']),
    currentPlaybackType: 'harmonic',
    melodicDirection: 'both', // 'ascending', 'descending', 'both'
    instrument: 'piano', // 'piano', 'sine', 'guitar', 'organ', 'flute'
    enabledOctaves: new Set([3, 4]), // Default to octaves 3 and 4
    volume: 0 // Default to full volume (0dB)
  });
  // Only the synthetic tones make the tuning audible; the toggle is hidden otherwise
  const tuningAvailable = config.instrument === 'pure_tone' || config.instrument === 'rich_tone';
  
  const audioContextRef = useRef(null);
  const osmdContainerRef = useRef(null);
  const osmdRef = useRef(null);
  const externalOsmdContainer = useRef(null);

  const instruments = {
    random: {
      name: `🎲 ${t('earTraining.instruments.random')}`,
      soundfont: null // Special case for random selection
    },
    rich_tone: {
      name: `〰️ ${t('earTraining.instruments.richTone')}`,
      soundfont: RICH_TONE
    },
    pure_tone: {
      name: `〰️ ${t('earTraining.instruments.pureTone')}`,
      soundfont: PURE_TONE
    },
    piano: {
      name: `🎹 ${t('earTraining.instruments.piano')}`,
      soundfont: 'piano'
    },
    electric_piano: {
      name: `🎹 ${t('earTraining.instruments.electricPiano')}`,
      soundfont: 'electric_piano'
    },
    guitar: {
      name: `🎸 ${t('earTraining.instruments.guitar')}`,
      soundfont: 'guitar'
    },
    bass: {
      name: `🎸 ${t('earTraining.instruments.bass')}`,
      soundfont: 'bass'
    },
    strings: {
      name: `🎻 ${t('earTraining.instruments.strings')}`,
      soundfont: 'strings'
    },
    violin: {
      name: `🎻 ${t('earTraining.instruments.violin')}`,
      soundfont: 'violin'
    },
    cello: {
      name: `🎻 ${t('earTraining.instruments.cello')}`,
      soundfont: 'cello'
    },
    brass: {
      name: `🎺 ${t('earTraining.instruments.brass')}`,
      soundfont: 'brass'
    },
    french_horn: {
      name: `🎺 ${t('earTraining.instruments.frenchHorn')}`,
      soundfont: 'french_horn'
    },
    trombone: {
      name: `🎺 ${t('earTraining.instruments.trombone')}`,
      soundfont: 'trombone'
    },
    saxophone: {
      name: `🎷 ${t('earTraining.instruments.saxophone')}`,
      soundfont: 'saxophone'
    },
    flute: {
      name: `🎵 ${t('earTraining.instruments.flute')}`,
      soundfont: 'flute'
    },
    clarinet: {
      name: `🎵 ${t('earTraining.instruments.clarinet')}`,
      soundfont: 'clarinet'
    },
    oboe: {
      name: `🎵 ${t('earTraining.instruments.oboe')}`,
      soundfont: 'oboe'
    },
    organ: {
      name: `🎼 ${t('earTraining.instruments.organ')}`,
      soundfont: 'organ'
    },
    choir: {
      name: `👥 ${t('earTraining.instruments.choir')}`,
      soundfont: 'choir'
    },
    bells: {
      name: `🔔 ${t('earTraining.instruments.bells')}`,
      soundfont: 'bells'
    },
    harp: {
      name: `🪕 ${t('earTraining.instruments.harp')}`,
      soundfont: 'harp'
    }
  };

  // List of instruments for random selection (excluding 'random' itself)
  const randomInstrumentList = Object.keys(instruments).filter(key => key !== 'random' && key !== 'pure_tone' && key !== 'rich_tone');

  const intervals = [
    { id: 'unison', name: t('earTraining.intervals.unison'), shortName: 'U', semitones: 0 },
    { id: 'minor2nd', name: t('earTraining.intervals.minor2nd'), shortName: 'm2', semitones: 1 },
    { id: 'major2nd', name: t('earTraining.intervals.major2nd'), shortName: 'M2', semitones: 2 },
    { id: 'minor3rd', name: t('earTraining.intervals.minor3rd'), shortName: 'm3', semitones: 3 },
    { id: 'major3rd', name: t('earTraining.intervals.major3rd'), shortName: 'M3', semitones: 4 },
    { id: 'perfect4th', name: t('earTraining.intervals.perfect4th'), shortName: 'P4', semitones: 5 },
    { id: 'tritone', name: t('earTraining.intervals.tritone'), shortName: 'TT', semitones: 6 },
    { id: 'perfect5th', name: t('earTraining.intervals.perfect5th'), shortName: 'P5', semitones: 7 },
    { id: 'minor6th', name: t('earTraining.intervals.minor6th'), shortName: 'm6', semitones: 8 },
    { id: 'major6th', name: t('earTraining.intervals.major6th'), shortName: 'M6', semitones: 9 },
    { id: 'minor7th', name: t('earTraining.intervals.minor7th'), shortName: 'm7', semitones: 10 },
    { id: 'major7th', name: t('earTraining.intervals.major7th'), shortName: 'M7', semitones: 11 },
    { id: 'octave', name: t('earTraining.intervals.octave'), shortName: '8va', semitones: 12 },
    { id: 'minor9th', name: t('earTraining.intervals.minor9th'), shortName: 'm9', semitones: 13 },
    { id: 'major9th', name: t('earTraining.intervals.major9th'), shortName: 'M9', semitones: 14 },
    { id: 'minor10th', name: t('earTraining.intervals.minor10th'), shortName: 'm10', semitones: 15 },
    { id: 'major10th', name: t('earTraining.intervals.major10th'), shortName: 'M10', semitones: 16 },
    { id: 'perfect11th', name: t('earTraining.intervals.perfect11th'), shortName: 'P11', semitones: 17 },
    { id: 'tritone12th', name: t('earTraining.intervals.tritone12th'), shortName: 'TT12', semitones: 18 },
    { id: 'perfect12th', name: t('earTraining.intervals.perfect12th'), shortName: 'P12', semitones: 19 },
    { id: 'minor13th', name: t('earTraining.intervals.minor13th'), shortName: 'm13', semitones: 20 },
    { id: 'major13th', name: t('earTraining.intervals.major13th'), shortName: 'M13', semitones: 21 }
  ];

  const difficultyPresets = {
    easy: {
      name: t('earTraining.difficulty.beginner'),
      intervals: new Set(['perfect4th', 'perfect5th', 'octave']),
      octaves: new Set([3, 4])
    },
    medium: {
      name: t('earTraining.difficulty.intermediate'),
      intervals: new Set(['perfect4th', 'perfect5th', 'major3rd', 'minor3rd', 'major6th', 'minor6th', 'octave']),
      octaves: new Set([3, 4])
    },
    hard: {
      name: t('earTraining.difficulty.advanced'),
      intervals: new Set(['unison', 'minor2nd', 'major2nd', 'minor3rd', 'major3rd', 'perfect4th', 'perfect5th', 'minor6th', 'major6th', 'minor7th', 'major7th', 'octave']),
      octaves: new Set([2, 3, 4, 5])
    },
    killer: {
      name: t('earTraining.difficulty.killer'),
      intervals: new Set(intervals.map(i => i.id)),
      octaves: new Set([2, 3, 4, 5])
    }
  };

  const initializeAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  };

  // Load the appropriate instrument based on config
  const loadCurrentInstrument = async () => {
    try {
      await Tone.start(); // Ensure audio context is started
      
      let instrumentToLoad;
      if (config.instrument === 'random') {
        // Pick a random instrument for this question
        instrumentToLoad = randomInstrumentList[Math.floor(Math.random() * randomInstrumentList.length)];
        setCurrentRandomInstrument(instrumentToLoad);
      } else {
        instrumentToLoad = config.instrument;
        setCurrentRandomInstrument(null);
      }
      
      const soundfontKey = instruments[instrumentToLoad].soundfont;
      const instrument = await loadSoundfontInstrument(soundfontKey, config.volume);
      setLoadedInstrument(instrument);
      
      return instrument;
    } catch (error) {
      // Fallback to Tone.js if soundfont fails
      // Fallback to Tone.js synth
      const fallback = new Tone.Synth({ volume: config.volume }).toDestination();
      setLoadedInstrument(fallback);
      return fallback;
    }
  };

  // Convert frequency to MIDI note name for soundfont
  const frequencyToNote = (frequency) => {
    const A4 = 440;
    const C0 = A4 * Math.pow(2, -4.75);
    
    if (frequency <= 0) return 'C4';
    
    const h = Math.round(12 * Math.log2(frequency / C0));
    const octave = Math.floor(h / 12);
    const n = h % 12;
    const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    
    return noteNames[n] + octave;
  };

  // Generate MusicXML for the interval
  const generateIntervalMusicXML = (interval, type = 'harmonic', direction = 'ascending') => {
    const baseNote = 'C';
    // Use the octave from the current interval, or default to 4
    const baseOctave = interval.selectedOctave || 4;
    
    // Determine clef based on octave range to minimize ledger lines
    // Bass clef (F clef) for octaves 2-3, Treble clef (G clef) for octaves 4-5
    const useBassClef = baseOctave <= 3;
    const clefSign = useBassClef ? 'F' : 'G';
    const clefLine = useBassClef ? '4' : '2';
    
    // Calculate the higher note based on the interval
    const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const baseIndex = noteNames.indexOf(baseNote);
    const higherIndex = (baseIndex + interval.semitones) % 12;
    const higherOctave = baseOctave + Math.floor((baseIndex + interval.semitones) / 12);
    const higherNote = noteNames[higherIndex];

    // Convert sharp notes to flat notes for XML (more standard)
    const convertToFlat = (note) => {
      const sharpToFlat = {
        'C#': 'D♭', 'D#': 'E♭', 'F#': 'G♭', 'G#': 'A♭', 'A#': 'B♭'
      };
      return sharpToFlat[note] || note;
    };

    const baseNoteName = convertToFlat(baseNote);
    const higherNoteName = convertToFlat(higherNote);

    if (type === 'harmonic') {
      // Both notes played simultaneously
      return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 3.1 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">
<score-partwise version="3.1">
  <part-list>
    <score-part id="P1">
      <part-name>Interval Training</part-name>
    </score-part>
  </part-list>
  <part id="P1">
    <measure number="1">
      <attributes>
        <divisions>4</divisions>
        <key>
          <fifths>0</fifths>
        </key>
        <time>
          <beats>4</beats>
          <beat-type>4</beat-type>
        </time>
        <clef>
          <sign>${clefSign}</sign>
          <line>${clefLine}</line>
        </clef>
      </attributes>
      <note>
        <pitch>
          <step>${baseNoteName.charAt(0)}</step>
          ${baseNoteName.includes('♭') ? '<alter>-1</alter>' : baseNoteName.includes('#') ? '<alter>1</alter>' : ''}
          <octave>${baseOctave}</octave>
        </pitch>
        <duration>16</duration>
        <type>whole</type>
      </note>
      <note>
        <chord/>
        <pitch>
          <step>${higherNoteName.charAt(0)}</step>
          ${higherNoteName.includes('♭') ? '<alter>-1</alter>' : higherNoteName.includes('#') ? '<alter>1</alter>' : ''}
          <octave>${higherOctave}</octave>
        </pitch>
        <duration>16</duration>
        <type>whole</type>
      </note>
    </measure>
  </part>
</score-partwise>`;
    } else {
      // Notes played separately (melodic) - order depends on direction
      const firstNote = direction === 'descending' ? higherNoteName : baseNoteName;
      const firstOctave = direction === 'descending' ? higherOctave : baseOctave;
      const secondNote = direction === 'descending' ? baseNoteName : higherNoteName;
      const secondOctave = direction === 'descending' ? baseOctave : higherOctave;
      
      return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 3.1 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">
<score-partwise version="3.1">
  <part-list>
    <score-part id="P1">
      <part-name>Interval Training</part-name>
    </score-part>
  </part-list>
  <part id="P1">
    <measure number="1">
      <attributes>
        <divisions>4</divisions>
        <key>
          <fifths>0</fifths>
        </key>
        <time>
          <beats>4</beats>
          <beat-type>4</beat-type>
        </time>
        <clef>
          <sign>${clefSign}</sign>
          <line>${clefLine}</line>
        </clef>
      </attributes>
      <note>
        <pitch>
          <step>${firstNote.charAt(0)}</step>
          ${firstNote.includes('♭') ? '<alter>-1</alter>' : firstNote.includes('#') ? '<alter>1</alter>' : ''}
          <octave>${firstOctave}</octave>
        </pitch>
        <duration>8</duration>
        <type>half</type>
      </note>
      <note>
        <pitch>
          <step>${secondNote.charAt(0)}</step>
          ${secondNote.includes('♭') ? '<alter>-1</alter>' : secondNote.includes('#') ? '<alter>1</alter>' : ''}
          <octave>${secondOctave}</octave>
        </pitch>
        <duration>8</duration>
        <type>half</type>
      </note>
    </measure>
  </part>
</score-partwise>`;
    }
  };

  // Render the interval notation
  const renderIntervalNotation = async (interval, type = 'harmonic') => {
    
    if (!externalOsmdContainer.current || !interval) {
      return;
    }

    try {
      // Clear previous OSMD instance
      if (osmdRef.current) {
        try {
          osmdRef.current.clear();
        } catch (e) {
        }
        osmdRef.current = null;
      }

      // Create new OSMD instance using external container
      osmdRef.current = new OpenSheetMusicDisplay(externalOsmdContainer.current, {
        autoResize: true,
        backend: "svg",
        drawTitle: false,
      });
      
      // Set a smaller zoom level to make the notation more compact
      osmdRef.current.zoom = 0.6;

      // Generate and load the MusicXML
      const musicXML = generateIntervalMusicXML(interval, type, interval.direction);
      
      await osmdRef.current.load(musicXML);
      
      osmdRef.current.render();

      // Add mobile-friendly zoom
      if (window.innerWidth <= 768) {
        osmdRef.current.zoom = 0.5;
        osmdRef.current.render();
      } else {
        osmdRef.current.render();
      }

    } catch (error) { 
      console.error('Error rendering interval notation:', error);
    }
  };

  const generateRandomInterval = async () => {
    const availableIntervals = intervals.filter(interval => config.enabledIntervals.has(interval.id));
    if (availableIntervals.length === 0) return null;
    
    const randomInterval = availableIntervals[Math.floor(Math.random() * availableIntervals.length)];
    
    // Select random octave from enabled octaves
    const enabledOctavesArray = Array.from(config.enabledOctaves);
    if (enabledOctavesArray.length === 0) {
      // Fallback to octave 4 if no octaves are selected
      enabledOctavesArray.push(4);
    }
    const selectedOctave = enabledOctavesArray[Math.floor(Math.random() * enabledOctavesArray.length)];
    
    // Calculate base frequency for selected octave (C note in that octave)
    const C4_FREQ = 261.63; // C4 frequency
    const baseNote = C4_FREQ * Math.pow(2, selectedOctave - 4);
    
    // Determine playback type for this question
    let playbackType;
    if (config.mode === 'mixed') {
      playbackType = Math.random() < 0.5 ? 'harmonic' : 'melodic';
    } else {
      playbackType = config.mode;
    }
    
    // Determine direction for melodic intervals
    let direction = 'ascending'; // default
    if (playbackType === 'melodic') {
      if (config.melodicDirection === 'both') {
        direction = Math.random() < 0.5 ? 'ascending' : 'descending';
      } else {
        direction = config.melodicDirection;
      }
    }
    
    const newInterval = {
      ...randomInterval,
      baseNote,
      higherNote: baseNote * Math.pow(2, randomInterval.semitones / 12),
      selectedOctave,
      playbackType,
      direction
    };
    
    // Load instrument for this question (includes random selection if needed)
    await loadCurrentInstrument();
    
    setCurrentInterval(newInterval);
    setFeedback(null);
    setHasGuessed(false);
    setShowInterval(false);
    
    // Update current playback type for display
    setConfig(prev => ({ ...prev, currentPlaybackType: playbackType }));
    
    // Force React to recreate the container to avoid DOM conflicts
    setNotationKey(prev => prev + 1);
    
    return newInterval;
  };

  const createGuessedInterval = (guessedIntervalId) => {
    const guessedIntervalData = intervals.find(i => i.id === guessedIntervalId);
    if (!guessedIntervalData || !currentInterval) return null;

    let baseNote, higherNote;

    if (currentInterval.direction === 'descending') {
      // For descending: keep the first (higher) note constant, change the second (lower) note
      // If original was G→C (desc P5), and guess is P4, should be G→D (desc P4)
      higherNote = currentInterval.higherNote; // Keep the starting note (G in example)
      baseNote = higherNote / Math.pow(2, guessedIntervalData.semitones / 12); // Calculate new lower note (D)
    } else {
      // For ascending/harmonic: keep the first (lower) note constant, change the second (higher) note
      // If original was C→G (asc P5), and guess is P4, should be C→F (asc P4)
      baseNote = currentInterval.baseNote; // Keep the first note (C)
      higherNote = baseNote * Math.pow(2, guessedIntervalData.semitones / 12); // Calculate new higher note (F)
    }

    const guessedInterval = {
      ...guessedIntervalData,
      baseNote,
      higherNote,
      playbackType: currentInterval.playbackType,
      direction: currentInterval.direction
    };

    return guessedInterval;
  };

  const highlightNote = (noteIndex, duration = 2000) => {
    if (!osmdRef.current || !externalOsmdContainer.current) return;
    
    try {
      // Find all note elements in the SVG
      const svg = externalOsmdContainer.current.querySelector('svg');
      if (!svg) return;
      
      const noteElements = svg.querySelectorAll('.vf-notehead, .vf-stem, .vf-flag');
      
      // Reset all notes to default color
      noteElements.forEach(el => {
        el.style.fill = 'black';
        el.style.stroke = 'black';
      });
      
      // Highlight specific note (0 = first note, 1 = second note)
      if (noteIndex >= 0 && noteIndex < 2) {
        const noteGroups = svg.querySelectorAll('g[class*="vf-note"]');
        if (noteGroups[noteIndex]) {
          const noteGroup = noteGroups[noteIndex];
          const elements = noteGroup.querySelectorAll('.vf-notehead, .vf-stem, .vf-flag');
          elements.forEach(el => {
            el.style.fill = '#ff4444';
            el.style.stroke = '#ff4444';
          });
          
          // Reset color after duration
          setTimeout(() => {
            elements.forEach(el => {
              el.style.fill = 'black';
              el.style.stroke = 'black';
            });
          }, duration);
        }
      }
    } catch (error) {
    }
  };

  const playInterval = async (type = 'harmonic', intervalToPlay = null) => {
    const interval = intervalToPlay || currentInterval;
    if (!interval || !loadedInstrument) return;
    
    setIsPlaying(true);
    
    try {
      await Tone.start(); // Ensure audio context is started
      
      const baseNoteName = frequencyToNote(interval.baseNote);
      const higherNoteName = frequencyToNote(interval.higherNote);
      const cents = intervalCentsOffset(interval.semitones, tuning);
      
      if (type === 'harmonic') {
        // Play both notes simultaneously using soundfont instrument
        loadedInstrument.triggerAttackRelease(baseNoteName, 2);
        loadedInstrument.triggerAttackRelease(higherNoteName, 2, undefined, 1, cents);
        
        // Highlight both notes simultaneously for harmonic intervals
        highlightNote(0, 2000);
        highlightNote(1, 2000);
        
        setTimeout(() => {
          setIsPlaying(false);
          setPlayingSource(null);
        }, 2000);
        
      } else {
        // Play notes sequentially (melodic) using soundfont instrument
        
        // Play notes in the correct order based on direction
        if (interval.direction === 'descending') {
          loadedInstrument.triggerAttackRelease(higherNoteName, 1, Tone.now(), 1, cents);
          loadedInstrument.triggerAttackRelease(baseNoteName, 1, Tone.now() + 1);
          
          // Highlight notes sequentially for descending melodic intervals
          highlightNote(1, 1000);
          setTimeout(() => highlightNote(0, 1000), 1000);
        } else {
          // Default ascending
          loadedInstrument.triggerAttackRelease(baseNoteName, 1, Tone.now());
          loadedInstrument.triggerAttackRelease(higherNoteName, 1, Tone.now() + 1, 1, cents);
          
          // Highlight notes sequentially for ascending melodic intervals
          highlightNote(0, 1000);
          setTimeout(() => highlightNote(1, 1000), 1000);
        }
        
        setTimeout(() => {
          setIsPlaying(false);
          setPlayingSource(null);
        }, 2000);
      }
    } catch (error) {
      setIsPlaying(false);
      setPlayingSource(null);
    }
  };

  const playArpeggiatedInterval = async (intervalToPlay = null) => {
    const interval = intervalToPlay || currentInterval;
    if (!interval || !loadedInstrument) return;
    
    setIsPlaying(true);
    
    try {
      await Tone.start(); // Ensure audio context is started
      
      const baseNoteName = frequencyToNote(interval.baseNote);
      const higherNoteName = frequencyToNote(interval.higherNote);
      const cents = intervalCentsOffset(interval.semitones, tuning);
      
      // Play arpeggiated pattern: base -> higher -> base (e.g., C -> G -> C)
      const noteDuration = 0.6; // Slightly shorter notes for quicker arpeggio
      const noteGap = 0.1; // Small gap between notes
      
      // First note (base)
      loadedInstrument.triggerAttackRelease(baseNoteName, noteDuration, Tone.now());
      highlightNote(0, noteDuration * 1000);
      
      // Second note (higher)
      loadedInstrument.triggerAttackRelease(higherNoteName, noteDuration, Tone.now() + noteDuration + noteGap, 1, cents);
      setTimeout(() => highlightNote(1, noteDuration * 1000), (noteDuration + noteGap) * 1000);
      
      // Third note (base again)
      loadedInstrument.triggerAttackRelease(baseNoteName, noteDuration, Tone.now() + (noteDuration + noteGap) * 2);
      setTimeout(() => highlightNote(0, noteDuration * 1000), (noteDuration + noteGap) * 2 * 1000);
      
      // Total duration: 3 notes + 2 gaps
      const totalDuration = (noteDuration * 3 + noteGap * 2) * 1000;
      
      setTimeout(() => {
        setIsPlaying(false);
        setPlayingSource(null);
      }, totalDuration);
      
    } catch (error) {
      setIsPlaying(false);
      setPlayingSource(null);
    }
  };

  // This function is no longer needed since we handle submission directly in handleIntervalSelect

  // Game flow functions
  const startGame = async () => {
    setGameState('playing');
    setCurrentQuestion(0);
    setScore({ correct: 0, total: 0 });
    setRoundResults([]);
    
    // Log training session start
    await logEarTrainingStart(user, 'Interval Training', config);
    
    await generateRandomInterval();
  };

  const handleIntervalSelect = (intervalId) => {
    if (!currentInterval || hasGuessed) return;
    
    const isCorrect = intervalId === currentInterval.id;
    const newScore = {
      correct: score.correct + (isCorrect ? 1 : 0),
      total: score.total + 1
    };
    
    setScore(newScore);
    setFeedback({ 
      isCorrect, 
      correctAnswer: currentInterval.name,
      userAnswer: intervals.find(i => i.id === intervalId)?.name || intervalId
    });
    setHasGuessed(true);
    setShowInterval(true);
    
    // Store the user's guessed interval for A/B comparison
    if (!isCorrect) {
      setUserGuessedInterval(createGuessedInterval(intervalId));
    }
    
    // Render the notation now that the user has guessed
    renderIntervalNotation(currentInterval, currentInterval.playbackType);
    
    // Add to round results
    setRoundResults(prev => [...prev, {
      question: currentQuestion + 1,
      interval: currentInterval,
      userAnswer: intervals.find(i => i.id === intervalId)?.name || intervalId,
      correct: isCorrect
    }]);
  };

  const handleABModeToggle = () => {
    setIsInABMode(true);
  };

  const handleExitABMode = () => {
    setIsInABMode(false);
    handleNextQuestion();
  };

  const playCurrentInterval = () => {
    if (currentInterval) {
      setPlayingSource('A');
      playInterval(currentInterval.playbackType, currentInterval);
    }
  };

  const playGuessedInterval = () => {
    if (userGuessedInterval) {
      setPlayingSource('B');
      playInterval(userGuessedInterval.playbackType, userGuessedInterval);
    }
  };

  const handleNextQuestion = async () => {
    if (currentQuestion + 1 >= config.questionCount) {
      setGameState('finished');
      
      // Log training session completion
      await logEarTrainingComplete(user, 'Interval Training', {
        correct: score.correct,
        total: score.total,
        roundResults
      });
    } else {
      setCurrentQuestion(prev => prev + 1);
      setIsInABMode(false);
      setUserGuessedInterval(null);
      await generateRandomInterval();
    }
  };

  const resetToSetup = () => {
    setGameState('setup');
    setCurrentQuestion(0);
    setScore({ correct: 0, total: 0 });
    setRoundResults([]);
    setCurrentInterval(null);
    setFeedback(null);
  };

  const applyPreset = (presetKey) => {
    const preset = difficultyPresets[presetKey];
    if (preset) {
      setConfig(prev => ({
        ...prev,
        enabledIntervals: new Set(preset.intervals),
        enabledOctaves: new Set(preset.octaves)
      }));
    }
  };

  const toggleInterval = (intervalId) => {
    setConfig(prev => {
      const newIntervals = new Set(prev.enabledIntervals);
      if (newIntervals.has(intervalId)) {
        newIntervals.delete(intervalId);
      } else {
        newIntervals.add(intervalId);
      }
      return { ...prev, enabledIntervals: newIntervals };
    });
  };

  const clearAllIntervals = () => {
    setConfig(prev => ({
      ...prev,
      enabledIntervals: new Set()
    }));
  };

  const invertIntervalSelection = () => {
    setConfig(prev => {
      const allIntervalIds = new Set(intervals.map(i => i.id));
      const currentlyEnabled = prev.enabledIntervals;
      const inverted = new Set();
      
      allIntervalIds.forEach(id => {
        if (!currentlyEnabled.has(id)) {
          inverted.add(id);
        }
      });
      
      return { ...prev, enabledIntervals: inverted };
    });
  };

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (loadedInstrument && loadedInstrument.dispose) {
        loadedInstrument.dispose();
      }
    };
  }, []);

  // Load instrument when configuration changes
  useEffect(() => {
    if (gameState === 'playing') {
      loadCurrentInstrument();
    }
  }, [config.instrument, config.volume]);

  // Auto-play when a new interval is generated in playing state
  useEffect(() => {
    if (gameState === 'playing' && currentInterval && !hasGuessed) {
      const timer = setTimeout(() => {
        playInterval(currentInterval.playbackType || 'harmonic');
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [currentInterval, gameState, hasGuessed]);

  // Create external container for OSMD outside React's DOM management
  useEffect(() => {
    if (osmdContainerRef.current && !externalOsmdContainer.current) {
      // Create a div that will hold OSMD content
      externalOsmdContainer.current = document.createElement('div');
      externalOsmdContainer.current.style.width = '100%';
      externalOsmdContainer.current.style.height = '100%';
      osmdContainerRef.current.appendChild(externalOsmdContainer.current);
    }

    return () => {
      if (externalOsmdContainer.current && osmdContainerRef.current) {
        try {
          osmdContainerRef.current.removeChild(externalOsmdContainer.current);
        } catch (e) {
          // Ignore errors during cleanup
        }
        externalOsmdContainer.current = null;
      }
    };
  }, [notationKey, showInterval]); // Add showInterval to dependencies

  // Render notation only AFTER user guesses (when showInterval is true)
  useEffect(() => {
    if (currentInterval && showInterval) {
      // Use a small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        // Ensure external container exists first
        if (osmdContainerRef.current && !externalOsmdContainer.current) {
          externalOsmdContainer.current = document.createElement('div');
          externalOsmdContainer.current.style.width = '100%';
          externalOsmdContainer.current.style.height = '100%';
          osmdContainerRef.current.appendChild(externalOsmdContainer.current);
        }
        
        if (externalOsmdContainer.current) {
          renderIntervalNotation(currentInterval, currentInterval.playbackType);
        } else {
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentInterval, notationKey, showInterval]);

  const renderConfigArea = () => {
    const isConfigDisabled = gameState === 'playing';
    
    return (
      <div className={`interval-config ${isConfigDisabled ? 'disabled' : ''}`}>
        <div className="config-header">
          <h4>{t('earTraining.config.title')}</h4>
        </div>
        
        {/* Mode Selection */}
        <div className="config-section">
          <label>{t('earTraining.config.playbackMode')}</label>
          <div className="mode-buttons">
            {['harmonic', 'melodic', 'mixed'].map((mode) => (
              <button
                key={mode}
                className={`mode-btn ${config.mode === mode ? 'active' : ''}`}
                onClick={() => setConfig(prev => ({ ...prev, mode }))}
                disabled={isConfigDisabled}
              >
                {mode === 'harmonic' ? `🎵 ${t('earTraining.modes.harmonic')}` : mode === 'melodic' ? `🎶 ${t('earTraining.modes.melodic')}` : `🔀 ${t('earTraining.modes.mixed')}`}
              </button>
            ))}
          </div>
        </div>

        {/* Melodic Direction - only show when melodic or mixed mode is selected */}
        {(config.mode === 'melodic' || config.mode === 'mixed') && (
          <div className="config-section">
            <label>{t('earTraining.config.melodicDirection')}:</label>
            <div className="mode-buttons">
              {[
                { value: 'ascending', label: `↗️ ${t('earTraining.directions.ascending')}`, icon: '↗️' },
                { value: 'descending', label: `↙️ ${t('earTraining.directions.descending')}`, icon: '↙️' },
                { value: 'both', label: `↕️ ${t('earTraining.directions.both')}`, icon: '↕️' }
              ].map((direction) => (
                <button
                  key={direction.value}
                  className={`mode-btn ${config.melodicDirection === direction.value ? 'active' : ''}`}
                  onClick={() => setConfig(prev => ({ ...prev, melodicDirection: direction.value }))}
                  disabled={isConfigDisabled}
                >
                  {direction.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Instrument Selection */}
        <div className="config-section">
          <label>{t('earTraining.config.instrument')}:</label>
          <select 
            value={config.instrument} 
            onChange={(e) => setConfig(prev => ({ ...prev, instrument: e.target.value }))}
          >
            {Object.entries(instruments).map(([key, instrument]) => (
              <option key={key} value={key}>
                {instrument.name}
              </option>
            ))}
          </select>
        </div>

        {/* Volume Control */}
        <div className="config-section">
          <label>{t('earTraining.config.volume')}:</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input 
              type="range" 
              min="-40" 
              max="6" 
              value={config.volume}
              step="1"
              onChange={(e) => setConfig(prev => ({...prev, volume: Number(e.target.value)}))}
              style={{ flex: 1 }}
            />
            <span className="volume-value" style={{ minWidth: '50px', fontSize: '12px' }}>
              {config.volume} dB
            </span>
          </div>
        </div>

        {/* Octave Selection */}
        <div className="config-section">
          <label>{t('earTraining.config.octaveRange')}:</label>
          <div className="interval-checkboxes-compact">
            {[2, 3, 4, 5].map((octave) => (
              <label key={octave} className="interval-checkbox-compact" title={`Octave ${octave}`}>
                <input
                  type="checkbox"
                  checked={config.enabledOctaves.has(octave)}
                  onChange={() => {
                    const newOctaves = new Set(config.enabledOctaves);
                    if (newOctaves.has(octave)) {
                      // Don't allow removing all octaves
                      if (newOctaves.size > 1) {
                        newOctaves.delete(octave);
                      }
                    } else {
                      newOctaves.add(octave);
                    }
                    setConfig(prev => ({ ...prev, enabledOctaves: newOctaves }));
                  }}
                  disabled={isConfigDisabled}
                />
                <span>{octave}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Question Count */}
        <div className="config-section">
          <label>{t('earTraining.config.questions')}:</label>
          <select 
            value={config.questionCount} 
            onChange={(e) => setConfig(prev => ({ ...prev, questionCount: parseInt(e.target.value) }))}
            disabled={isConfigDisabled}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
          </select>
        </div>

        {/* Difficulty Presets */}
        <div className="config-section">
          <label>{t('earTraining.config.difficultyPresets')}:</label>
          <div className="preset-buttons">
            {Object.entries(difficultyPresets).map(([key, preset]) => (
              <button
                key={key}
                className="preset-btn"
                onClick={() => applyPreset(key)}
                disabled={isConfigDisabled}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>

        {/* Interval Selection */}
        <div className="config-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <label>{t('earTraining.config.intervalsToInclude')}:</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={clearAllIntervals}
                disabled={isConfigDisabled}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 6px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px'
                }}
                title="Clear all intervals"
              >
                🗑️
              </button>
              <button
                onClick={invertIntervalSelection}
                disabled={isConfigDisabled}
                style={{
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 6px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px'
                }}
                title="Invert selection"
              >
                🔄
              </button>
            </div>
          </div>
          <div className="interval-checkboxes-compact">
            {intervals.map((interval) => (
              <label key={interval.id} className="interval-checkbox-compact" title={interval.name}>
                <input
                  type="checkbox"
                  checked={config.enabledIntervals.has(interval.id)}
                  onChange={() => toggleInterval(interval.id)}
                  disabled={isConfigDisabled}
                />
                <span>{interval.shortName}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderPlayArea = () => {
    if (gameState === 'setup') {
      return (
        <div className="play-area setup">
          <div className="setup-message">
            <h3>{t('earTraining.setup.readyToTrain')}</h3>
            <p>{t('earTraining.setup.configureAndStart')}</p>
            <button 
              className="start-game-btn"
              onClick={startGame}
              disabled={config.enabledIntervals.size === 0}
            >
              {t('earTraining.setup.startTraining')}
            </button>
          </div>
        </div>
      );
    }

    if (gameState === 'finished') {
      return (
        <div className="play-area finished">
          <div className="results-summary">
            <h3>{t('earTraining.results.roundComplete')}</h3>
            <div className="final-score">
              <div className="score-circle">
                <span className="score-percentage">{Math.round((score.correct / score.total) * 100)}%</span>
                <span className="score-fraction">{score.correct}/{score.total}</span>
              </div>
            </div>
            <div className="results-breakdown">
              {roundResults.map((result, index) => (
                <div key={index} className={`result-item ${result.correct ? 'correct' : 'incorrect'}`}>
                  <span className="question-num">{t('earTraining.results.questionAbbrev')}{result.question}</span>
                  <span className="interval-name">{result.interval.name}</span>
                  <span className="result-icon">{result.correct ? '✅' : '❌'}</span>
                </div>
              ))}
            </div>
            <div className="result-actions">
              <button className="play-again-btn" onClick={startGame}>{t('earTraining.results.playAgain')}</button>
              <button className="setup-btn" onClick={resetToSetup}>{t('earTraining.results.changeSettings')}</button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`play-area playing ${feedback?.isCorrect === true ? 'feedback-correct' : feedback?.isCorrect === false ? 'feedback-incorrect' : ''}`}>
        <div className="game-header">
          <div className="progress">
            Question {currentQuestion + 1} of {config.questionCount}
            {config.instrument === 'random' && currentRandomInstrument && (
              <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                {instruments[currentRandomInstrument]?.name}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="current-score" style={{
              background: '#1e293b',
              color: '#f1f5f9',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #475569'
            }}>
              <span className="score-label" style={{color: '#94a3b8', fontSize: '12px', marginRight: '8px'}}>Score:</span>
              <span className="score-value" style={{color: '#60a5fa', fontWeight: 'bold', fontSize: '16px'}}>{score.correct}/{score.total}</span>
            </div>
            <button 
              onClick={resetToSetup}
              style={{
                background: '#dc2626',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.background = '#b91c1c'}
              onMouseOut={(e) => e.target.style.background = '#dc2626'}
              title={t('earTraining.gameplay.quitAndReturn')}
            >
              {t('earTraining.gameplay.quit')}
            </button>
          </div>
        </div>

        {!showInterval ? (
          <div className="pre-guess-display">
            <div className="guess-prompt">
              <h3>{t('earTraining.gameplay.listenAndGuess')}</h3>
              <p>
                {t('earTraining.gameplay.identifyThe')}{' '}
                <strong>{currentInterval?.playbackType === 'harmonic' ? t('earTraining.intervals.harmonic') : t('earTraining.intervals.melodic')}</strong>{' '}
                {t('earTraining.gameplay.interval')}
              </p>
              <div className="wave-animation">
                <span>♪</span><span>♫</span><span>♪</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="notation-container" style={{ minHeight: '60px', padding: '8px', margin: '8px 0' }}>
            <div className="interval-notation-wrapper">
              <div className="interval-title">
                <h4>
                  {currentInterval?.name} - {' '}
                  {currentInterval?.playbackType === 'harmonic' 
                    ? t('earTraining.intervals.harmonic') 
                    : `${t('earTraining.intervals.melodic')} (${
                        currentInterval?.direction === 'ascending' ? `↗️ ${t('earTraining.directions.ascending')}` : 
                        currentInterval?.direction === 'descending' ? `↙️ ${t('earTraining.directions.descending')}` : 
                        t('earTraining.intervals.melodic')
                      })`
                  }
                </h4>
              </div>
              
              <div 
                key={notationKey}
                ref={osmdContainerRef} 
                className="interval-notation"
                style={{
                  backgroundColor: 'white',
                  color: 'black',
                  filter: 'none',
                  position: 'relative',
                  minHeight: '60px',
                  maxHeight: '80px',
                  padding: '4px'
                }}
              >
                {/* This will be replaced by OSMD content */}
              </div>
            </div>
          </div>
        )}
        
        <>
          {/* A/B Comparison Mode */}
          {feedback && isInABMode && !feedback.isCorrect && userGuessedInterval && (
            <div style={{
              margin: '8px 0',
              padding: '12px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: 'white',
              boxShadow: '0 4px 16px rgba(30, 41, 59, 0.3)'
            }}>
              <div style={{marginBottom: '12px', fontSize: '14px', fontWeight: '600', textAlign: 'center', textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'}}>
                🎵 {t('earTraining.gameplay.abComparisonMode')}
              </div>
              
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px'}}>
                <div style={{textAlign: 'center', padding: '8px', background: '#1f2937', borderRadius: '6px', border: '1px solid #16a34a', borderLeft: '4px solid #16a34a'}}>
                  <div style={{fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#86efac'}}>
                    A: {t('earTraining.gameplay.correctAnswer')}
                  </div>
                  <div style={{fontSize: '12px', marginBottom: '6px', color: '#d1d5db'}}>
                    {currentInterval?.name}
                  </div>
                  <button 
                    onClick={playCurrentInterval}
                    disabled={isPlaying}
                    style={{
                      background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                      color: '#86efac',
                      border: '1px solid #16a34a',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}
                  >
                    {playingSource === 'A' ? t('earTraining.gameplay.playing') : `🔊 ${t('earTraining.gameplay.playA')}`}
                  </button>
                </div>
                
                <div style={{textAlign: 'center', padding: '8px', background: '#1f2937', borderRadius: '6px', border: '1px solid #dc2626', borderLeft: '4px solid #dc2626'}}>
                  <div style={{fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#fca5a5'}}>
                    B: {t('earTraining.gameplay.yourGuess')}
                  </div>
                  <div style={{fontSize: '12px', marginBottom: '6px', color: '#d1d5db'}}>
                    {userGuessedInterval?.name}
                  </div>
                  <button 
                    onClick={playGuessedInterval}
                    disabled={isPlaying}
                    style={{
                      background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                      color: '#fca5a5',
                      border: '1px solid #dc2626',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}
                  >
                    {playingSource === 'B' ? t('earTraining.gameplay.playing') : `🔊 ${t('earTraining.gameplay.playB')}`}
                  </button>
                </div>
              </div>
              
              <div style={{textAlign: 'center'}}>
                <button 
                  onClick={handleExitABMode}
                  style={{
                    background: 'linear-gradient(135deg, #475569 0%, #64748b 100%)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  {currentQuestion + 1 >= config.questionCount ? t('earTraining.gameplay.finish') : t('earTraining.gameplay.next')} →
                </button>
              </div>
            </div>
          )}
          
          {/* Regular feedback area */}
          {feedback && !isInABMode && (
            <div style={{
              margin: '8px 0',
              padding: '12px',
              borderRadius: '8px',
              textAlign: 'center',
              backgroundColor: feedback.isCorrect ? '#1f2937' : '#1f2937',
              border: feedback.isCorrect ? '2px solid #16a34a' : '2px solid #dc2626',
              color: feedback.isCorrect ? '#86efac' : '#fca5a5',
              boxShadow: feedback.isCorrect ? '0 4px 12px rgba(22, 163, 74, 0.15)' : '0 4px 12px rgba(220, 38, 38, 0.15)'
            }}>
              <div style={{marginBottom: '10px', fontSize: '16px', fontWeight: 'bold'}}>
                {feedback.isCorrect ? `✅ ${t('earTraining.gameplay.correct')}` : `❌ ${t('earTraining.gameplay.incorrect')}`}
                {!feedback.isCorrect && (
                  <div style={{fontSize: '13px', marginTop: '4px', fontWeight: 'normal'}}>
                    {t('earTraining.gameplay.correctAnswerLabel')}: <strong>{feedback.correctAnswer}</strong>
                  </div>
                )}
              </div>
              
              <div style={{display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap'}}>
                <button 
                  onClick={() => playInterval(currentInterval?.playbackType || 'harmonic')}
                  disabled={isPlaying}
                  style={{
                    background: 'linear-gradient(135deg, #475569 0%, #64748b 100%)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isPlaying ? t('earTraining.gameplay.playing') : `🔊 ${t('earTraining.gameplay.playAgain')}`}
                </button>
                
                {!feedback.isCorrect && currentInterval?.playbackType === 'harmonic' && (
                  <button 
                    onClick={() => playArpeggiatedInterval()}
                    disabled={isPlaying}
                    style={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {isPlaying ? t('earTraining.gameplay.playing') : `🎶 Arpeggiate`}
                  </button>
                )}
                
                {!feedback.isCorrect && userGuessedInterval && (
                  <button 
                    onClick={handleABModeToggle}
                    style={{
                      background: '#fb923c',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}
                  >
                    🎵 {t('earTraining.gameplay.abItForMe')}
                  </button>
                )}
                
                <button 
                  onClick={handleNextQuestion}
                  style={{
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}
                >
                  {currentQuestion + 1 >= config.questionCount ? t('earTraining.gameplay.finish') : t('earTraining.gameplay.next')} →
                </button>
              </div>
            </div>
          )}

          {!feedback && (
            <div className="play-controls">
              <button 
                className="play-interval-btn"
                onClick={() => playInterval(currentInterval?.playbackType || 'harmonic')}
                disabled={!currentInterval || isPlaying}
              >
                {isPlaying ? t('earTraining.gameplay.playing') : `🔊 ${t('earTraining.gameplay.play')} ${currentInterval?.playbackType === 'harmonic' ? t('earTraining.intervals.harmonic') : t('earTraining.intervals.melodic')}`}
              </button>
            </div>
          )}
        </>

        {tuningAvailable && (
          <div className="tuning-toggle compact" role="group" aria-label={t('earTraining.config.tuning')} title={t('earTraining.config.tuningHint')}>
            <span className="tuning-toggle-label">{t('earTraining.config.tuning')}:</span>
            <button
              type="button"
              className={tuning === TUNINGS.equal ? 'active' : ''}
              aria-pressed={tuning === TUNINGS.equal}
              onClick={() => setTuning(TUNINGS.equal)}
            >
              {t('earTraining.config.tuningEqual')}
            </button>
            <button
              type="button"
              className={tuning === TUNINGS.just ? 'active' : ''}
              aria-pressed={tuning === TUNINGS.just}
              onClick={() => setTuning(TUNINGS.just)}
            >
              {t('earTraining.config.tuningJust')}
            </button>
          </div>
        )}

        <div className="interval-choices">
          {intervals.filter(i => config.enabledIntervals.has(i.id)).map((interval) => (
            <button
              key={interval.id}
              className="interval-choice"
              onClick={() => handleIntervalSelect(interval.id)}
              disabled={hasGuessed}
              style={{ display: feedback ? 'none' : 'block' }}
            >
              {interval.name}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="interval-training-container">
      <div className="desktop-layout">
        <CollapsibleSection
          title={t('earTraining.config.title')}
          defaultExpanded={false}
          className="config-section-mobile"
        >
          {renderConfigArea()}
        </CollapsibleSection>
        
        <div className="config-area-desktop">
          {renderConfigArea()}
        </div>
        
        <div className="play-area-container">
          {renderPlayArea()}
        </div>
      </div>
    </div>
  );
};

export default IntervalTraining;